/**
 * RoleGuard Component
 * Guards a route or section based on the authenticated user's role(s).
 */

import { ReactNode } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { consoleRoutePaths, consoleRoutePatterns } from '@/navigation/consoleRoutePaths';
import { useAuthStore } from '../../stores/authStore';
import type { UserRole } from '../../types/auth';
import Card from '../ui/Card';

interface RoleGuardProps {
  roles: UserRole[];
  children: ReactNode;
  redirectTo?: string;
}

export default function RoleGuard({
  roles,
  children,
  redirectTo = consoleRoutePaths.devices,
}: RoleGuardProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const userRole = useAuthStore((state) => state.user?.role);
  const isOwner = useAuthStore((state) => state.isOwner);

  if (!isAuthenticated) {
    return <Navigate to={consoleRoutePatterns.login} state={{ from: location }} replace />;
  }

  const ownerAllowed = roles.includes('SUPERADMIN') && isOwner;
  const roleAllowed = userRole ? roles.some((role) => role !== 'SUPERADMIN' && role === userRole) : false;

  if (!ownerAllowed && !roleAllowed) {
    return (
      <div className="mx-auto w-full max-w-2xl px-6 py-10">
        <Card padding="lg" className="border-status-failed-border bg-status-failed-surface">
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-status-failed-surface">
              <svg
                className="h-7 w-7 text-status-failed"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-ink-strong">Access denied</h2>
            <p className="mt-2 text-sm text-ink">
              Your role does not allow this page. If you need it, ask your organization
              administrator to change your role.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="rounded-lg border border-border-strong px-4 py-2 text-sm font-medium text-ink hover:bg-surface-recessed"
              >
                Go back
              </button>
              <button
                type="button"
                onClick={() => navigate(redirectTo, { replace: true })}
                className="rounded-lg bg-action-danger px-4 py-2 text-sm font-medium text-on-danger hover:bg-action-danger"
              >
                {redirectTo === consoleRoutePaths.devices ? 'Go to Devices' : 'Go to start page'}
              </button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}
